import { defineStore } from 'pinia'
import { markRaw } from 'vue'
import type MapCoordinate from '@/types/MapCoordinate.ts'
import { useMapShipStore } from '@/stores/mapShipStore.ts'
import { usePlayerRelationStore } from '@/stores/playerRelations.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { useMapPlanetOwnerStore } from '@/stores/mapPlanetOwnerStore.ts'
import { useMapStore } from '@/stores/mapStore.ts'
import { useMapPlanetStore } from '@/stores/mapPlanetStore.ts'
import { useMapStarStore } from '@/stores/mapStarStore.ts'
import { useStarStore } from '@/stores/starStore.ts'
import { useMapPendingSettleStore } from '@/stores/mapPendingSettleStore.ts'
import Eta from '@/models/eta.ts'
import * as THREE from 'three'
import type MapPlanet from '@/types/MapPlanet.ts'
import { useMapPortalStore } from '@/stores/mapPortalStore.ts'

// Reusable Eta instance to avoid allocation during updates
const _eta = new Eta()

// Fast check if object has any keys (avoids Object.keys() array allocation)
function hasKeys(obj: Record<string | number, unknown>): boolean {
  for (const _ in obj) return true
  return false
}

// Fast comparison of ships objects to avoid unnecessary clear/set cycles
function shipsEqual(
  oldShips: Record<string | number, number>,
  newShips: Record<string | number, number>
): boolean {
  // Quick reference check
  if (oldShips === newShips) return true

  // Count keys in both objects
  let oldCount = 0
  let newCount = 0
  for (const _ in oldShips) oldCount++
  for (const _ in newShips) newCount++
  if (oldCount !== newCount) return false

  // Compare values
  for (const key in oldShips) {
    if (oldShips[key] !== newShips[key]) return false
  }
  return true
}

export const useMapCoordinateStore = defineStore('mapCoordinateStore', {
  state: () => ({
    coordinates: markRaw(new Map()),
    planets: markRaw(new Map()),
    planetsByStarId: markRaw(new Map<number, MapPlanet[]>()), // Index for fast star ownership lookups
    fleets: markRaw(new Map()),
    fleetsVersion: 0, // Reactive counter to trigger updates when raw fleets Map changes
    scene: markRaw(new THREE.Scene()),
    etaXPos: 0, //X Position for ETA calculations on sidebar
    etaYPos: 0, //Y Position for ETA calculations on sidebar
    etaSpeed: 0, //Speed for ETA calculations on sidebar
    // Batching state for WebSocket updates (processed by render loop)
    _pendingUpdates: markRaw<MapCoordinate[]>([])
  }),
  actions: {
    hasByCoordinates(x: number, y: number): boolean {
      const key = x + '-' + y
      return this.coordinates.has(key)
    },
    has(mapCoordinate: MapCoordinate): boolean {
      const key = mapCoordinate.xPos + '-' + mapCoordinate.yPos
      return this.coordinates.has(key)
    },
    get(mapCoordinate: MapCoordinate): any {
      const key = mapCoordinate.xPos + '-' + mapCoordinate.yPos
      return this.coordinates.get(key)
    },
    getByCoordinates(x: number, y: number): any {
      const key = x + '-' + y
      return this.coordinates.get(key)
    },
    getSettleablePlanets: function (): Array<any> {
      const planets: MapPlanet[] = []
      this.planets.forEach((value) => {
        if (value.playerId === 0 && value.type !== 9) {
          planets.push(value)
        }
      })
      return planets
    },
    /**
     * Get all planets belonging to a specific star
     * Uses the indexed map for O(1) lookup instead of O(n) iteration
     */
    getPlanetsForStar(starId: number): MapPlanet[] {
      return this.planetsByStarId.get(starId) || []
    },
    set(
      mapCoordinate: MapCoordinate,
      shipInstances: {
        playerShip: number | null
        enemyShip: number | null
        friendlyShip: number | null
        allyShip: number | null
        neutralShip: number | null
        allianceShip: number | null
      },
      planetOwnerInstance: Number | null,
      planetInstance: Number | null,
      starInstance: Number | null
    ) {
      const gameStore = useGameStore()

      const key = mapCoordinate.xPos + '-' + mapCoordinate.yPos

      this.coordinates.set(key, {
        id: key,
        mapCoordinate: mapCoordinate,
        shipInstances: shipInstances,
        planetOwnerInstance: planetOwnerInstance,
        planetInstance: planetInstance,
        starInstance: starInstance,
        eta: 0
      })

      //also store the planet data
      if (Number(mapCoordinate.type) === 2) {
        // Skip destroyed planets - don't add them to the planets map
        if (mapCoordinate.typeVariant === 9) {
          // Remove from index if it exists
          const oldPlanet = this.planets.get(key)
          if (oldPlanet && oldPlanet.starId !== undefined) {
            const starPlanets = this.planetsByStarId.get(oldPlanet.starId)
            if (starPlanets) {
              const index = starPlanets.findIndex(p => p.id === key)
              if (index !== -1) {
                starPlanets.splice(index, 1)
                if (starPlanets.length === 0) {
                  this.planetsByStarId.delete(oldPlanet.starId)
                }
              }
            }
          }
          this.planets.delete(key)
          return
        }

        const sizeCompatible = mapCoordinate.typeSize === gameStore.player?.homeWorldPlanetSize
        const sizeInCompatible =
          mapCoordinate.typeSize === gameStore.player?.homeWorldOppositePlanetSize
        const typeCompatible = mapCoordinate.typeVariant === gameStore.player?.homeWorldPlanetType
        const typeInCompatible =
          mapCoordinate.typeVariant === gameStore.player?.homeWorldOppositePlanetType

        let compatibility = 'C'
        if (mapCoordinate.typeVariant === 7) {
          compatibility = 'F'
        } else if (mapCoordinate.typeVariant === 8) {
          // RingWorld - always excellent for any species due to advanced technology
          compatibility = 'A'
        } else if (sizeCompatible && typeCompatible) {
          compatibility = 'A'
        } else if (sizeInCompatible && typeInCompatible) {
          compatibility = 'E'
        } else if ((sizeCompatible || typeCompatible) && !sizeInCompatible && !typeInCompatible) {
          compatibility = 'B'
        } else if ((sizeInCompatible || typeInCompatible) && !sizeCompatible && !typeCompatible) {
          compatibility = 'D'
        }

        // Skip ETA calculation when no speed is set (saves iteration work)
        let newETA = 0
        if (this.etaSpeed > 0) {
          _eta.start(mapCoordinate.xPos, mapCoordinate.yPos)
          newETA = _eta.etaNext(this.etaXPos, this.etaYPos, this.etaSpeed)
        }

        const newPlanet: MapPlanet = {
          id: key,
          planetId: mapCoordinate.typeId,
          playerId: mapCoordinate.typePlayerId,
          size: mapCoordinate.typeSize,
          type: mapCoordinate.typeVariant,
          xPos: mapCoordinate.xPos,
          yPos: mapCoordinate.yPos,
          visible: mapCoordinate.currentlyVisible,
          name: mapCoordinate.typeName,
          compatibility: compatibility,
          eta: newETA,
          starId: mapCoordinate.starId
        }

        // Update index: remove from old starId (if changed), add to new starId
        const oldPlanet = this.planets.get(key)
        if (oldPlanet && oldPlanet.starId !== undefined && oldPlanet.starId !== newPlanet.starId) {
          // StarId changed - remove from old star's index
          const oldStarPlanets = this.planetsByStarId.get(oldPlanet.starId)
          if (oldStarPlanets) {
            const index = oldStarPlanets.findIndex(p => p.id === key)
            if (index !== -1) {
              oldStarPlanets.splice(index, 1)
              if (oldStarPlanets.length === 0) {
                this.planetsByStarId.delete(oldPlanet.starId)
              }
            }
          }
        }

        // Add/update in new starId index
        if (newPlanet.starId !== undefined) {
          if (!this.planetsByStarId.has(newPlanet.starId)) {
            this.planetsByStarId.set(newPlanet.starId, [])
          }
          const starPlanets = this.planetsByStarId.get(newPlanet.starId)!
          const existingIndex = starPlanets.findIndex(p => p.id === key)
          if (existingIndex !== -1) {
            // Update existing planet in index
            starPlanets[existingIndex] = newPlanet
          } else {
            // Add new planet to index
            starPlanets.push(newPlanet)
          }
        }

        this.planets.set(key, newPlanet)
      } else {
        // Not a planet - remove from index if it was previously a planet
        const oldPlanet = this.planets.get(key)
        if (oldPlanet && oldPlanet.starId !== undefined) {
          const starPlanets = this.planetsByStarId.get(oldPlanet.starId)
          if (starPlanets) {
            const index = starPlanets.findIndex(p => p.id === key)
            if (index !== -1) {
              starPlanets.splice(index, 1)
              if (starPlanets.length === 0) {
                this.planetsByStarId.delete(oldPlanet.starId)
              }
            }
          }
        }
        this.planets.delete(key)
      }
    },
    recalculateMapEtas(xPos: number, yPos: number, maxSpeed: number) {
      this.etaXPos = xPos
      this.etaYPos = yPos
      this.etaSpeed = maxSpeed

      //Go through all planets and recalculate the ETA
      this.planets.forEach((value) => {
        _eta.start(xPos, yPos)
        value.eta = _eta.etaNext(value.xPos, value.yPos, maxSpeed)
      })
      //Go through all the ships and recalculate the ETA
      this.fleets.forEach((value) => {
        _eta.start(xPos, yPos)
        value.eta = _eta.etaNext(value.xPos, value.yPos, maxSpeed)
      })
    },
    resetMapEtas() {
      this.etaXPos = 0
      this.etaYPos = 0
      this.etaSpeed = 0
      this.planets.forEach((value) => {
        value.eta = 0
      })
      this.fleets.forEach((value) => {
        value.eta = 0
      })
    },
    getDataForRayCasting(x: number, y: number) {
      const key = x + '-' + y
      if (this.coordinates.has(key)) {
        const data = this.coordinates.get(key)
        const mc = data.mapCoordinate
        const mcType = mc.type
        const mcVariant = mc.typeVariant
        const isPlanet = mcType === 2 && mcVariant !== 9
        const ships = mc.ships
        const hasShips = hasKeys(ships)

        // Calculate compatibility for planets
        let compatibility = ''
        if (isPlanet) {
          const gameStore = useGameStore()
          const player = gameStore.player
          const sizeCompatible = mc.typeSize === player?.homeWorldPlanetSize
          const sizeInCompatible = mc.typeSize === player?.homeWorldOppositePlanetSize
          const typeCompatible = mcVariant === player?.homeWorldPlanetType
          const typeInCompatible = mcVariant === player?.homeWorldOppositePlanetType

          if (mcVariant === 7) {
            compatibility = 'F'
          } else if (mcVariant === 8) {
            // RingWorld - always excellent
            compatibility = 'A'
          } else if (sizeCompatible && typeCompatible) {
            compatibility = 'A'
          } else if (sizeInCompatible && typeInCompatible) {
            compatibility = 'E'
          } else if ((sizeCompatible || typeCompatible) && !sizeInCompatible && !typeInCompatible) {
            compatibility = 'B'
          } else if ((sizeInCompatible || typeInCompatible) && !sizeCompatible && !typeCompatible) {
            compatibility = 'D'
          } else {
            compatibility = 'C'
          }
        }

        return {
          id: mc.typeId ?? 0,
          type: mcType,
          typeName: mc.typeName,
          isPlanet: isPlanet,
          isStar: mcType === 1,
          isWormhole: mcType === 4,
          planetOwnerId: mc.typePlayerId,
          compatibility: compatibility,
          canBeSettled: mc.typePlayerId === 0 && isPlanet,
          canBeFollowed: hasShips,
          hasTooltip: mcType === 4 || isPlanet || mcType === 1 || hasShips,
          hasPlayerShips: ships[useGameStore().player?.id ?? 0] !== undefined,
          ships: ships,
          xPos: mc.xPos,
          yPos: mc.yPos
        }
      }
      return null
    },
    clearShips(data: any) {
      const mapShipStore = useMapShipStore()
      if (data.shipInstances.playerShip) {
        mapShipStore.removeShip(data.shipInstances.playerShip)
      }
      if (data.shipInstances.enemyShip) {
        mapShipStore.removeShip(data.shipInstances.enemyShip)
      }
      if (data.shipInstances.friendlyShip) {
        mapShipStore.removeShip(data.shipInstances.friendlyShip)
      }
      if (data.shipInstances.allyShip) {
        mapShipStore.removeShip(data.shipInstances.allyShip)
      }
      if (data.shipInstances.neutralShip) {
        mapShipStore.removeShip(data.shipInstances.neutralShip)
      }
      if (data.shipInstances.allianceShip) {
        mapShipStore.removeShip(data.shipInstances.allianceShip)
      }
    },
    setShips(data: MapCoordinate): {
      playerShip: number | null
      enemyShip: number | null
      friendlyShip: number | null
      allyShip: number | null
      neutralShip: number | null
      allianceShip: number | null
    } {
      const gameStore = useGameStore()
      const playerRelationStore = usePlayerRelationStore()
      const mapShipStore = useMapShipStore()

      let playerShips = 0
      let enemyShips = 0
      let friendlyShips = 0
      let allyShips = 0
      let neutralShips = 0
      let allianceShips = 0
      for (const playerId in data.ships) {
        const shipCount = data.ships[playerId]
        if (Number(playerId) == gameStore.player?.id) {
          playerShips = shipCount
        } else {
          const relation = playerRelationStore.findRelationByPlayerId(Number(playerId))
          if (relation.relation == 'enemy') {
            enemyShips += shipCount
          } else if (relation.relation == 'friend') {
            friendlyShips += shipCount
          } else if (relation.relation == 'ally') {
            allyShips += shipCount
          } else if (relation.relation == 'neutral') {
            neutralShips += shipCount
          } else if (relation.relation == 'alliance') {
            allianceShips += shipCount
          }
        }
      }

      let playerShipInstanceId = null
      if (playerShips > 0) {
        playerShipInstanceId = mapShipStore.addShip(data.xPos, data.yPos, 0x00ff00)
        // Set pulsating effect for large fleets (>49 ships)
        if (playerShipInstanceId !== null && playerShips > 49) {
          mapShipStore.setShipAsFleet(playerShipInstanceId, true)
        }
      }
      let enemyShipInstanceId = null
      if (enemyShips > 0) {
        enemyShipInstanceId = mapShipStore.addShip(data.xPos, data.yPos, 0xff0000)
        // Set pulsating effect for large fleets (>49 ships)
        if (enemyShipInstanceId !== null && enemyShips > 49) {
          mapShipStore.setShipAsFleet(enemyShipInstanceId, true)
        }
      }
      let friendlyShipInstanceId = null
      if (friendlyShips > 0) {
        friendlyShipInstanceId = mapShipStore.addShip(data.xPos, data.yPos, 0xce93d8)
        // Set pulsating effect for large fleets (>49 ships)
        if (friendlyShipInstanceId !== null && friendlyShips > 49) {
          mapShipStore.setShipAsFleet(friendlyShipInstanceId, true)
        }
      }
      let allyShipInstanceId = null
      if (allyShips > 0) {
        allyShipInstanceId = mapShipStore.addShip(data.xPos, data.yPos, 0x00ffff)
        // Set pulsating effect for large fleets (>49 ships)
        if (allyShipInstanceId !== null && allyShips > 49) {
          mapShipStore.setShipAsFleet(allyShipInstanceId, true)
        }
      }
      let neutralShipInstanceId = null
      if (neutralShips > 0) {
        neutralShipInstanceId = mapShipStore.addShip(data.xPos, data.yPos, 0xffff00)
        // Set pulsating effect for large fleets (>49 ships)
        if (neutralShipInstanceId !== null && neutralShips > 49) {
          mapShipStore.setShipAsFleet(neutralShipInstanceId, true)
        }
      }
      let allianceShipInstanceId = null
      if (allianceShips > 0) {
        allianceShipInstanceId = mapShipStore.addShip(data.xPos, data.yPos, 0x0000ff)
        // Set pulsating effect for large fleets (>49 ships)
        if (allianceShipInstanceId !== null && allianceShips > 49) {
          mapShipStore.setShipAsFleet(allianceShipInstanceId, true)
        }
      }

      return {
        playerShip: playerShipInstanceId,
        enemyShip: enemyShipInstanceId,
        friendlyShip: friendlyShipInstanceId,
        allyShip: allyShipInstanceId,
        neutralShip: neutralShipInstanceId,
        allianceShip: allianceShipInstanceId
      }
    },
    /**
     * Queue a map coordinate update for batched processing.
     * Updates are processed by the render loop via processPendingWithBudget().
     */
    process(data: MapCoordinate) {
      // Add to pending updates queue (will be processed by render loop)
      this._pendingUpdates.push(data)
    },

    /**
     * Process pending WebSocket updates within a time budget.
     * Called by the render loop to ensure 60fps is maintained.
     * @param budgetMs - Maximum time in milliseconds to spend processing
     * @returns Number of remaining items in the queue
     */
    processPendingWithBudget(budgetMs: number): number {
      if (this._pendingUpdates.length === 0) {
        return 0
      }

      const startTime = performance.now()
      let processed = 0

      while (this._pendingUpdates.length > 0) {
        // Check time budget before processing each item
        if (performance.now() - startTime >= budgetMs) {
          break
        }
        const data = this._pendingUpdates.shift()!
        this._processImmediate(data)
        processed++
      }

      // Update the instanced mesh matrices if we processed anything
      if (processed > 0) {
        const mapShipStore = useMapShipStore()
        if (mapShipStore.shipInstancedMesh) {
          mapShipStore.shipInstancedMesh.instanceMatrix.needsUpdate = true
        }
      }

      return this._pendingUpdates.length
    },

    /**
     * Get the number of pending updates in the queue.
     * Useful for monitoring queue depth.
     */
    getPendingCount(): number {
      return this._pendingUpdates.length
    },

    /**
     * Process ALL pending updates without budget constraint.
     * Used during initial load to maximize processing speed.
     */
    processAllPending(): void {
      if (this._pendingUpdates.length === 0) {
        return
      }

      while (this._pendingUpdates.length > 0) {
        const data = this._pendingUpdates.shift()!
        this._processImmediate(data)
      }

      // Batch update matrices once at end
      const mapShipStore = useMapShipStore()
      if (mapShipStore.shipInstancedMesh) {
        mapShipStore.shipInstancedMesh.instanceMatrix.needsUpdate = true
      }
    },

    /**
     * Immediately process a single map coordinate update.
     * This is the original process logic, now called from the batch processor.
     */
    _processImmediate(data: MapCoordinate) {
      if (this.has(data)) {
        //Need to update

        /** Ships **/
        const oldData = this.get(data)

        // Check if ships actually changed to avoid expensive clear/set cycle
        const shipsChanged = !shipsEqual(oldData.mapCoordinate.ships, data.ships)

        if (shipsChanged) {
          this.clearShips(oldData)

          /** Update FleetMap Data **/
          // check for new ships
          for (const playerId in data.ships) {
            const shipCount = data.ships[playerId]
            const playerIdNum = Number(playerId)
            // New/update Fleet

            // Skip ETA calculation when no speed is set
            let newETA = 0
            if (this.etaSpeed > 0) {
              _eta.start(data.xPos, data.yPos)
              newETA = _eta.etaNext(this.etaXPos, this.etaYPos, this.etaSpeed)
            }

            const key = data.xPos + '-' + data.yPos + '-' + playerIdNum
            this.fleets.set(key, {
              id: key,
              playerId: playerIdNum,
              xPos: data.xPos,
              yPos: data.yPos,
              ships: shipCount,
              eta: newETA
            })
            this.fleetsVersion++
          }
          // Check for removed ships
          for (const playerId in oldData.mapCoordinate.ships) {
            const playerIdNum = Number(playerId)
            if (data.ships[playerIdNum] === undefined) {
              // Removed Ship
              const key =
                oldData.mapCoordinate.xPos + '-' + oldData.mapCoordinate.yPos + '-' + playerIdNum
              this.fleets.delete(key)
              this.fleetsVersion++
            }
          }
        }

        //if old data shows wormhole, but new data does not, remove the portal
        if (oldData.mapCoordinate.type == 4 && Number(data.type) != 4) {
          const mapPortalStore = useMapPortalStore()
          const instanceId = mapPortalStore.findInstanceIdByCoordinates(
            oldData.mapCoordinate.xPos,
            oldData.mapCoordinate.yPos
          )
          mapPortalStore.removePortal(instanceId)
        }

        //If this is open space, without ships, remove it
        if (Number(data.type) == 0 && !hasKeys(data.ships)) {
          //Remove
          //console.log(data,Object.keys(data.ships).length)
          this.coordinates.delete(data.xPos + '-' + data.yPos)
          return
        }

        //Update Ships (only if changed)
        const shipInstances = shipsChanged ? this.setShips(data) : oldData.shipInstances

        // Check if planet became destroyed
        if (Number(data.type) == 2 && data.typeVariant === 9) {
          // Planet became destroyed - remove from visual map
          if (oldData.planetInstance !== null) {
            const mapPlanetStore = useMapPlanetStore()
            mapPlanetStore.removePlanet(oldData.planetInstance)
          }
          // Remove planet owner circle
          if (oldData.planetOwnerInstance !== null) {
            const mapPlanetOwnerStore = useMapPlanetOwnerStore()
            mapPlanetOwnerStore.removePlanetOwner(oldData.planetOwnerInstance)
          }
          
          // Still need to update the coordinate data
          this.set(
            data,
            shipInstances,
            null, // No planet owner instance
            null, // No planet instance
            oldData.starInstance
          )
          
          const mapStore = useMapStore()
          this.applyZoomFactor(
            {
              mapCoordinate: data,
              shipInstances: shipInstances,
              planetOwnerInstance: null,
              planetInstance: null,
              starInstance: oldData.starInstance
            },
            mapStore.zoomFactor
          )
          return
        }

        /** Planet Owner Circle **/
        const mapPlanetOwnerStore = useMapPlanetOwnerStore()
        let planetOwnerInstance = oldData.planetOwnerInstance
        if (oldData.planetOwnerInstance !== null) {
          mapPlanetOwnerStore.removePlanetOwner(oldData.planetOwnerInstance)
          planetOwnerInstance = mapPlanetOwnerStore.addPlanetOwner(
            data.xPos,
            data.yPos,
            mapPlanetOwnerStore.getColor(data)
          )
        }

        /** Overwrite Data **/
        this.set(
          data,
          shipInstances,
          planetOwnerInstance,
          oldData.planetInstance,//not sure if this needed to be fixed
          oldData.starInstance
        )

        const mapStore = useMapStore()
        this.applyZoomFactor(
          {
            mapCoordinate: data,
            shipInstances: shipInstances,
            planetOwnerInstance: planetOwnerInstance,
            planetInstance: oldData.planetInstance,
            starInstance: oldData.starInstance
          },
          mapStore.zoomFactor
        )
      } else {
        // Create new

        /** Ships **/
        const shipInstances = this.setShips(data)

        /** Planet **/
        let planetInstance = null
        if (Number(data.type) == 2 && data.typeVariant !== 9) {
          const mapPlanetStore = useMapPlanetStore()
          planetInstance = mapPlanetStore.addPlanet(
            data.xPos,
            data.yPos,
            data.typeVariant,
            data.typeSize
          )
        }
        /** Planet Owner Circle **/
        let planetOwnerInstance = null
        if (Number(data.type) == 2 && data.typeVariant !== 9) {
          const mapPlanetOwnerStore = useMapPlanetOwnerStore()
          const mapPlanetOwnerColor = mapPlanetOwnerStore.getColor(data)
          planetOwnerInstance = mapPlanetOwnerStore.addPlanetOwner(
            data.xPos,
            data.yPos,
            mapPlanetOwnerColor
          )
        }

        /** Star **/
        let starInstance = null
        if (Number(data.type) == 1) {
          const mapStarStore = useMapStarStore()
          starInstance = mapStarStore.addStar(data.xPos, data.yPos, data.typeVariant, data.typeSize)

          // Store star data in starStore
          const starStore = useStarStore()
          starStore.updateStarById(data.typeId, {
            id: data.typeId,
            name: data.typeName,
            type: data.typeVariant,
            size: data.typeSize,
            xPos: data.xPos,
            yPos: data.yPos,
            playerId: 0
          })
        }

        /** Portal **/
        if (Number(data.type) == 4) {
          const mapPortalStore = useMapPortalStore()
          mapPortalStore.addPortal(data.xPos, data.yPos)
        }

        /** Update FleetMap Data **/
        // check for new ships
        let fleetsAdded = false
        for (const playerId in data.ships) {
          const shipCount = data.ships[playerId]
          const playerIdNum = Number(playerId)
          // New/update Fleet
          const key = data.xPos + '-' + data.yPos + '-' + playerIdNum
          this.fleets.set(key, {
            id: key,
            playerId: playerIdNum,
            xPos: data.xPos,
            yPos: data.yPos,
            ships: shipCount
          })
          fleetsAdded = true
        }
        if (fleetsAdded) {
          this.fleetsVersion++
        }

        /** Store Data **/
        this.set(data, shipInstances, planetOwnerInstance, planetInstance, starInstance)

        const mapStore = useMapStore()
        this.applyZoomFactor(
          {
            mapCoordinate: data,
            shipInstances: shipInstances,
            planetOwnerInstance: planetOwnerInstance,
            planetInstance: planetInstance,
            starInstance: starInstance
          },
          mapStore.zoomFactor
        )
      }
      //Make sure the new element has the correct zoomFactor applied
    },
    applyZoomFactor(data: any, zoom: number) {
      const shipInstances = data.shipInstances
      //on each zoomFactor we need to move things around or resize
      const mapShipStore = useMapShipStore()

      /** Ships Scaling and Positioning **/
      let position = 0
      const totalPositions =
        (shipInstances.playerShip !== null ? 1 : 0) +
        (shipInstances.enemyShip !== null ? 1 : 0) +
        (shipInstances.friendlyShip !== null ? 1 : 0) +
        (shipInstances.allyShip !== null ? 1 : 0) +
        (shipInstances.neutralShip !== null ? 1 : 0) +
        (shipInstances.allianceShip !== null ? 1 : 0)

      if (shipInstances.playerShip !== null) {
        position++
        mapShipStore.scaleAndPositionShip(
          shipInstances.playerShip,
          data.mapCoordinate,
          totalPositions,
          position,
          zoom
        )
      }
      if (shipInstances.enemyShip !== null) {
        position++
        mapShipStore.scaleAndPositionShip(
          shipInstances.enemyShip,
          data.mapCoordinate,
          totalPositions,
          position,
          zoom
        )
      }
      if (shipInstances.friendlyShip !== null) {
        position++
        mapShipStore.scaleAndPositionShip(
          shipInstances.friendlyShip,
          data.mapCoordinate,
          totalPositions,
          position,
          zoom
        )
      }
      if (shipInstances.allyShip !== null) {
        position++
        mapShipStore.scaleAndPositionShip(
          shipInstances.allyShip,
          data.mapCoordinate,
          totalPositions,
          position,
          zoom
        )
      }
      if (shipInstances.neutralShip !== null) {
        position++
        mapShipStore.scaleAndPositionShip(
          shipInstances.neutralShip,
          data.mapCoordinate,
          totalPositions,
          position,
          zoom
        )
      }
      if (shipInstances.allianceShip !== null) {
        position++
        mapShipStore.scaleAndPositionShip(
          shipInstances.allianceShip,
          data.mapCoordinate,
          totalPositions,
          position,
          zoom
        )
      }

      /** Planet Owner Circle Scaling and Positioning **/
      if (data.planetOwnerInstance !== null) {
        const mapPlanetOwnerStore = useMapPlanetOwnerStore()
        mapPlanetOwnerStore.scaleAndPositionPlanetOwner(
          data.planetOwnerInstance,
          data.mapCoordinate,
          zoom
        )
      }
      /** Planet Does not Scale **/

      /** Star Does not Scale **/
    },
    applyZoomFactorAll(zoom: number) {
      this.coordinates.forEach((value) => {
        this.applyZoomFactor(value, zoom)
      })

      /** Settle Indicator does Scale **/
      const mapPendingSettleStore = useMapPendingSettleStore()
      mapPendingSettleStore.scaleAndPositionObjects(zoom)
    },
    reDrawPlayerAssets(playerId: number) {
      //Redraw all assets for a player, (because of a change in relation)
      this.coordinates.forEach((value) => {
        if (
          value.mapCoordinate.ships[playerId] !== undefined ||
          value.mapCoordinate.typePlayerId == playerId
        ) {
          this.process(value.mapCoordinate)
        }
      })
    }
  }
})
